import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import styled from '@emotion/styled';
import { ReactNode } from 'react';
import { numberComma } from '@diritto/common/utils/string';
import { ShareIcon } from 'icons/ShareIcon';
import { RollingRelayDto } from '@diritto/common/relay/dtos/rolling-relay.dtos';
import { TrackerPosition } from '@diritto/common/tracker/tracking-event.constant';
import { css } from '@emotion/react';
import { RollingReactionProvider, useRollingReaction } from 'contexts/RollingReactionProvider';
import { RollingReactionType } from '@diritto/common/rolling/constants';
import { useAuthState } from 'contexts/AuthProvider';
import { RollingReactionDto } from '@diritto/common/rolling/dtos/rolling-reaction.dto';

const trackerExtraInfo = {
  trackerPosition: TrackerPosition.RollingViewer,
};

const Reaction = ({
  icon,
  title,
  rollingReactionType,
}: {
  icon: ReactNode;
  title: string;
  rollingReactionType: RollingReactionType;
}) => {
  const { count, isOn, action } = useRollingReaction(rollingReactionType);

  return (
    <StyledReaction onClick={action}>
      <StyledReactionIcon>{icon}</StyledReactionIcon>
      <StyledReactionText isOn={isOn}>{title}</StyledReactionText>
      <StyledReactionCount isOn={isOn}>{numberComma(count)}</StyledReactionCount>
    </StyledReaction>
  );
};

const ShareReaction = ({ icon, title }: { icon: ReactNode; title: string }) => {
  const { count, action } = useRollingReaction('Share');

  return (
    <StyledReaction onClick={action}>
      <StyledReactionIcon>{icon}</StyledReactionIcon>
      <StyledReactionText>{title}</StyledReactionText>
      <StyledReactionCount>{numberComma(count)}</StyledReactionCount>
    </StyledReaction>
  );
};

export const RollingReactions = ({
  rolling,
  rollingReaction,
}: {
  rolling: RollingRelayDto;
  rollingReaction?: RollingReactionDto;
}) => {
  const { isLoggedIn, isLoaded: isAuthLoaded } = useAuthState();

  return (
    <StyledReactionContainer>
      {((isAuthLoaded && !isLoggedIn) || rollingReaction) && (
        <RollingReactionProvider
          rolling={rolling}
          rollingReaction={rollingReaction}
          trackerExtraInfo={trackerExtraInfo}>
          <Reaction title="또보고싶어요" icon="🥹" rollingReactionType={RollingReactionType.Favorite} />
          <Reaction title="취향저격" icon="💘" rollingReactionType={RollingReactionType.Recommend} />
          <Reaction title="흥미진진" icon="🎵" rollingReactionType={RollingReactionType.Excited} />
          <ShareReaction title="공유" icon={<ShareIcon className="size-[24px]" color={Color.Black} />} />
        </RollingReactionProvider>
      )}
    </StyledReactionContainer>
  );
};

const StyledReactionContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  padding: 16px 0;
  margin-top: 24px;

  border-top: 1px solid ${Color.Gray200};
  border-bottom: 1px solid ${Color.Gray200};
`;

const StyledReaction = styled.div`
  flex: 1 1 0;

  display: flex;
  align-items: center;
  flex-direction: column;

  &:not(:first-of-type) {
    border-left: 1px solid ${Color.Gray200};
  }
`;

const StyledReactionIcon = styled.div`
  height: 30px;

  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;

  ${Typography.Medium};
`;

const StyledReactionText = styled.div(
  ({ isOn }: { isOn?: boolean }) => css`
    width: 100%;
    text-align: center;
    color: ${isOn ? Color.Primary500 : Color.Gray700};
    ${Typography.XXSmall};
    font-weight: ${FontWeight.Bold};
  `,
);

const StyledReactionCount = styled.div(
  ({ isOn }: { isOn?: boolean }) => css`
    width: 100%;
    text-align: center;
    color: ${isOn ? Color.Primary500 : Color.Gray600};
    ${Typography.XXSmall};
    font-weight: ${FontWeight.Medium};
  `,
);
